import { reflect } from '@effector/reflect';
import { useUnit } from 'effector-react';
import { CircleSlash2 } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { translated } from 'shared/lib';
import { ConfirmModalProps, createConfirmModal } from 'shared/ui';
import { UnblockCashdeskModel } from '../../model';

export const createUnblockCashdeskModal = (model: UnblockCashdeskModel) => {
	const ConfirmModal = createConfirmModal(model);

	const UnblockCashdeskModal = forwardRef<HTMLDivElement, ConfirmModalProps>(
		({ ...props }, ref) => {
			const { t } = useTranslation();

			const [cashdesk] = useUnit([model.$modalData]);

			return (
				<ConfirmModal
					ref={ref}
					title={t('unblock-cashdesk.title')}
					cancelButtonProps={{
						children: t('unblock-cashdesk.cancel'),
					}}
					submitButtonProps={{
						variant: 'outline',
						children: t('unblock-cashdesk.unblock'),
					}}
					{...props}
				>
					{t('unblock-cashdesk.description', { name: cashdesk?.name })}
				</ConfirmModal>
			);
		},
	);

	const UnblockCashdeskModalButton = reflect({
		view: ConfirmModal.Button,
		bind: {
			'data-test-id': 'unblock-cashdesk-button',
			variant: 'outline',
			disabled: not(viewerModel.$canWriteCashdesks),
			leftSection: <CircleSlash2 />,
			children: translated('unblock-cashdesk.unblock'),
		},
	});

	const UnblockCashdeskModalActionIcon = reflect({
		view: ConfirmModal.ActionIcon,
		bind: {
			'data-test-id': 'unblock-cashdesk-action-icon',
			variant: 'ghost-destructive',
			disabled: not(viewerModel.$canWriteCashdesks),
			children: <CircleSlash2 />,
		},
	});

	return Object.assign(UnblockCashdeskModal, {
		Button: UnblockCashdeskModalButton,
		ActionIcon: UnblockCashdeskModalActionIcon,
	});
};
